import React from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import ContactForm from './components/ContactForm';
import ContactMethods from './components/ContactMethods';
import AvailabilityStatus from './components/AvailabilityStatus';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';
import { useGlobalContext } from '../../context/GlobalContext';

const ContactPage = () => {
  const { globalSettings: settings } = useGlobalContext();

  return (
    <div className="min-h-screen bg-transparent text-text-primary">
      <Helmet>
        <title>Contact – Sudhan R | Collaborate on STEM, Space-Tech & Development Projects</title>
        <meta
          name="description"
          content="Reach out to Sudhan R for research collaborations, space-tech projects, STEM outreach, or full-stack development. Free consultation, confidential communication, and flexible availability."
        />
        <meta
          name="keywords"
          content="Contact Sudhan R, STEM collaboration, ISRO intern, NASA tutor, remote sensing, web development, project consultation, tech mentorship, Tamil Nadu developer, scientific outreach"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Contact – Sudhan R | Let’s Build Something Amazing Together" />
        <meta
          property="og:description"
          content="Connect with Sudhan R to collaborate on research, tech, or outreach projects. Get expert guidance on STEM, space innovation, or web development."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sudhan.live/contact" />
        <meta property="og:image" content="https://sudhan.live/assets/images/Sudhan_2026_v3.png" />
        <meta property="og:locale" content="en_IN" />
        <meta name="author" content="Sudhan R" />
        <link rel="canonical" href="https://sudhan.live/contact" />

        {/* Structured Data (JSON-LD) */}
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "ContactPage",
                "@id": "https://sudhan.live/contact#webpage",
                "url": "https://sudhan.live/contact",
                "name": "Contact - Sudhan R",
                "description": "Connect with Sudhan R. Contact methods, availability status for collaboration, LinkedIn profile, and contact details.",
                "isPartOf": {
                  "@id": "https://sudhan.live/#website"
                },
                "mainEntity": {
                  "@id": "https://sudhan.live/#person"
                },
                "breadcrumb": {
                  "@id": "https://sudhan.live/contact#breadcrumb"
                }
              },
              {
                "@type": "BreadcrumbList",
                "@id": "https://sudhan.live/contact#breadcrumb",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": "https://sudhan.live/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Contact",
                    "item": "https://sudhan.live/contact"
                  }
                ]
              }
            ]
          }
          `}
        </script>
      </Helmet>

      <Header />

      <section className="pt-24 lg:pt-32 pb-12 lg:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 opacity-50"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="font-heading font-bold text-4xl lg:text-5xl xl:text-6xl text-text-primary mb-6">
              Let's Build Something
              <span className="text-accent block">Amazing Together</span>
            </h1>
            <p className="text-lg lg:text-xl text-text-secondary mb-8 leading-relaxed">
              Ready to start your project? Choose your preferred way to connect and let's discuss 
              how we can bring your vision to life through clear communication and collaborative development.
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-text-muted">
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} />
                <span>Free initial consultation</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={16} />
                <span>Confidential discussions</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={16} />
                <span>No commitment required</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-16 lg:pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-12">
            {/* Contact Form - Takes up 2 columns on large screens */}
            <div className="lg:col-span-2">
              <ContactForm />
            </div>

            {/* Sidebar - Availability and Contact Methods */}
            <div className="space-y-8">
              <AvailabilityStatus />
              <div className="lg:hidden">
                <ContactMethods />
              </div>
            </div>
          </div>

          {/* Contact Methods - Hidden on mobile, shown on desktop */}
          <div className="hidden lg:block mt-12">
            <ContactMethods />
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 lg:py-24 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/10 opacity-50"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10 bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 lg:p-12 shadow-2xl">
          <div className="mb-8">
            <h2 className="font-heading font-bold text-3xl lg:text-4xl text-text-primary mb-4">
              Ready to Launch Something Amazing?
            </h2>
            <p className="text-lg text-text-secondary mb-8">
              Every meaningful collaboration starts with a simple message. Whether it's a research idea, a tech project, or a space to inspire young minds—let's connect and explore it together.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={`mailto:${settings?.contact_email || 'sudhan.r.web@gmail.com'}`}
              className="inline-flex items-center space-x-2 bg-accent text-white font-heading font-semibold px-8 py-4 rounded-lg hover:bg-accent-600 transition-smooth shadow-lg hover:shadow-accent/40 transform hover:-translate-y-0.5"
              aria-label="Send a direct email to Sudhan R"
              title={settings?.contact_email || "sudhan.r.web@gmail.com"}
            >
              <Icon name="Mail" size={20} />
              <span>Send an Email</span>
            </a>
            <a
              href={`https://wa.me/${(settings?.contact_phone || '919940980978').replace(/[-+ \(\)]/g, '')}`}
              className="inline-flex items-center space-x-2 bg-transparent border-2 border-white/20 text-text-primary font-heading font-semibold px-8 py-4 rounded-lg hover:bg-white/10 transition-smooth"
              aria-label="Call or message Sudhan R directly"
              title={settings?.contact_phone || "+91 99409 80978"}
            >
              <Icon name="Phone" size={20} />
              <span>Call Now</span>
            </a>
          </div>

          <div className="mt-8 pt-8 border-t border-white/10">
            <div className="flex flex-wrap justify-center gap-6 text-text-muted">
              <div className="flex items-center space-x-2">
                <Icon name="MapPin" size={16} />
                <span>Tamil Nadu, India </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Globe" size={16} />
                <span>Available Remotely Worldwide  </span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Clock" size={16} />
                <span>IST Timezone (UTC+5:30)</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;